/**
 * FCADetailPanel — Full First Cut Analysis detail view
 * Authority: FCA_SKILL_DOC_ADDITIONS.md + DESIGN_LANGUAGE_SKILL.md
 * Tokens: ui-invariants.js (DS_COLORS, DS_TYPOGRAPHY, DS_SPACING, DS_BORDERS, ACTION_BUTTONS, VERDICT_PILL)
 * Config: task-display-config.js (FCA_PAGE_CONFIG, INTENT_TO_BUTTON)
 *
 * Receives startupId and trigger props. Calls getFCAResult on mount.
 * Zero hardcoded hex values. Zero inline label strings. 44px min all interactive.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CaretRight, SpinnerGap, FilePdf, X as XIcon } from '@phosphor-icons/react';
import {
  getFCAResult,
  getFCAStatus,
  rerunFca,
  exportFCAPdf,
} from '@/lib/api';
import { FCA_PAGE_CONFIG, INTENT_TO_BUTTON } from '@/config/task-display-config';
import {
  DS_COLORS,
  DS_TYPOGRAPHY,
  DS_SPACING,
  DS_BORDERS,
  ACTION_BUTTONS,
  VERDICT_PILL,
  AI_REWRITING_STATE,
} from '@/config/ui-invariants';
import { AccrezeoSheet } from '@/components/shared/AccrezeoSheet';
import { toast } from 'sonner';

/**
 * SpinIcon — replaces `animation: 'spin 1s linear infinite'` with Framer Motion.
 * Motion is the single animation runtime per .impeccable.md.
 */
function SpinIcon({ size = 16, color }) {
  return (
    <motion.span
      aria-hidden="true"
      style={{ display: 'inline-flex', lineHeight: 0 }}
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
    >
      <SpinnerGap size={size} color={color} />
    </motion.span>
  );
}

/**
 * PulseOpacity — replaces inline `@keyframes pulse` opacity loops.
 * Opacity-only per .impeccable.md motion rules.
 */
function PulseOpacity({ children, min = 0.3, max = 0.6, duration = 1.5, style }) {
  return (
    <motion.div
      style={style}
      animate={{ opacity: [max, min, max] }}
      transition={{ repeat: Infinity, duration, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
}

// ── Normalisation utility (Fix 5) ───────────────────────────
function normaliseToArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split('\n').filter((line) => line.trim());
  return [];
}

// ── Config lookups ──────────────────────────────────────────
const LABELS = FCA_PAGE_CONFIG;
const FCA_BUTTONS = FCA_PAGE_CONFIG.fcaButtons || {};
const FCA_ADJUST = FCA_PAGE_CONFIG.fcaAdjustPanel || {};
const FCA_TABLE = FCA_PAGE_CONFIG.fcaParameterTable || {};

const RECOMMENDATION_COLOR = {
  positive: DS_COLORS.text.success,
  cautious: DS_COLORS.ai.action,
  negative: DS_COLORS.text.error,
  neutral:  DS_COLORS.text.secondary,
};

const SIGNAL_TYPE_LABELS = {
  belief_evolve: 'Belief',
  risk_track: 'Risk',
  tracklist: 'Watchlist',
};

const VERDICT_OPTIONS = FCA_PAGE_CONFIG.parameterVerdictOptions || [];

const NEXT_DATA_ASK_HEADING = {
  PRE: 'Questions for the Founder Call',
  POST: 'Follow-Up Requests',
};

const FCA_KNOB_DEFS = FCA_PAGE_CONFIG.fcaKnobDefs || [];

// ── Helpers ─────────────────────────────────────────────────

function resolveVerdict(param) {
  if (param.verdict) return param.verdict;
  if (param.provisional_verdict) return param.provisional_verdict;
  return 'unclear';
}

// ── Skeleton loader ─────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{ maxWidth: `${DS_SPACING.pageMaxWidth}px`, padding: `${DS_SPACING.cardPadding}px` }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <PulseOpacity
          key={i}
          min={0.3}
          max={0.6}
          duration={1.5}
          style={{
            height: '20px',
            background: DS_COLORS.surface.slate,
            borderRadius: DS_BORDERS.radiusCard,
            marginBottom: `${DS_SPACING.labelToContent}px`,
          }}
        />
      ))}
    </div>
  );
}

// ── Pipeline polling status ─────────────────────────────────

function PipelineStatus({ startupId, trigger, onComplete, onFailed }) {
  const [phase, setPhase] = useState(null);
  const intervalRef = useRef(null);
  const progressStates = FCA_PAGE_CONFIG.progressStates[trigger || 'pre'] || [];

  useEffect(() => {
    const poll = async () => {
      try {
        const data = await getFCAStatus(startupId);
        setPhase(data);
        if (data?.state === 'complete' || data?.state === 'failed' || data?.state === 'partial') {
          clearInterval(intervalRef.current);
          if (data.state === 'complete' || data.state === 'partial') {
            onComplete();
          } else if (data.state === 'failed') {
            onFailed?.(data.error_message || data.fca_error || '');
          }
        }
      } catch {
        // DECISION: swallow polling errors, retry on next interval
      }
    };
    poll();
    intervalRef.current = setInterval(poll, 3000);
    return () => clearInterval(intervalRef.current);
  }, [startupId, onComplete, onFailed]);

  const resolveLabel = () => {
    if (!phase) {
      return progressStates[0]?.label || 'Analysing';
    }
    if (phase.state === 'complete') {
      return FCA_PAGE_CONFIG.completeLabel;
    }
    const currentKey = phase.current_phase;
    if (currentKey) {
      const match = progressStates.find((s) => s.key === currentKey);
      if (match) return match.label;
    }
    return progressStates[progressStates.length - 1]?.label || 'Analysing';
  };

  return (
    <div style={{
      padding: `${DS_SPACING.cardPadding}px`,
      background: DS_COLORS.surface.warm,
      border: `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
      borderRadius: DS_BORDERS.radiusCard,
      marginBottom: `${DS_SPACING.cardGap}px`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: `${DS_SPACING.buttonGap}px` }}>
        <div style={{
          width: '8px', height: '8px', borderRadius: '50%',
          background: DS_COLORS.text.brand,
          animation: AI_REWRITING_STATE.pulseAnimation,
        }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{
            ...DS_TYPOGRAPHY.sectionLabel,
            color: DS_COLORS.text.primary,
          }}>
            {resolveLabel()}
          </span>
          {phase?.progress_detail && (
            <span style={{
              fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
              fontSize: '13px',
              fontWeight: 400,
              color: DS_COLORS.text.secondary,
            }}>
              {phase.progress_detail}
            </span>
          )}
        </div>
      </div>
      {/* Token-authored AI rewrite pulse keyframes stay — registry authority.
          Legacy inline `@keyframes spin` removed; spinner markup uses <SpinIcon /> (Framer Motion). */}
      <style>{AI_REWRITING_STATE.pulseKeyframes}</style>
    </div>
  );
}

// ── Verdict Pill Component ──────────────────────────────────

function VerdictPillBadge({ verdict }) {
  const v = VERDICT_PILL[verdict] || VERDICT_PILL.unclear;
  return (
    <span style={{
      ...VERDICT_PILL.pill,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: v.background,
      color: v.text,
      border: `1px solid ${v.border}`,
    }}>
      <span style={{
        width: v.dotSize,
        height: v.dotSize,
        borderRadius: '50%',
        background: v.dot,
        flexShrink: 0,
      }} />
      {v.label}
    </span>
  );
}

// ── Parameter Table Row (vertical 3-column) ─────────────────

function ParameterTableRow({ param, trigger }) {
  const [open, setOpen] = useState(false);
  const verdict = resolveVerdict(param);
  const showAnnotation = trigger === 'POST' && param.pre_call_annotation;

  return (
    <>
      {/* Row */}
      <tr
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer', borderBottom: `1px solid ${DS_BORDERS.color}`, height: '56px' }}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
      >
        {/* Column 1: Parameter name */}
        <td style={{
          fontFamily: DS_TYPOGRAPHY.sectionTitle.fontFamily,
          fontSize: '14px',
          fontWeight: 600,
          color: DS_COLORS.text.primary,
          padding: `${DS_SPACING.labelToContent}px ${DS_SPACING.cardPadding}px`,
          minHeight: '56px',
          verticalAlign: 'middle',
        }}>
          {param.parameter_name}
        </td>

        {/* Column 2: Verdict pill */}
        <td style={{
          textAlign: 'center',
          padding: `${DS_SPACING.labelToContent}px ${DS_SPACING.buttonGap}px`,
          verticalAlign: 'middle',
        }}>
          <VerdictPillBadge verdict={verdict} />
        </td>

        {/* Column 3: Finding (headline) */}
        <td style={{
          ...DS_TYPOGRAPHY.bodyMd,
          color: param.headline ? DS_COLORS.text.primary : DS_COLORS.text.muted,
          padding: `${DS_SPACING.labelToContent}px ${DS_SPACING.cardPadding}px`,
          verticalAlign: 'middle',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: `${DS_SPACING.buttonGap}px` }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              {param.headline || ''}
            </span>
            <CaretRight
              weight="bold"
              size={14}
              style={{
                color: DS_COLORS.text.muted,
                flexShrink: 0,
                transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </div>
        </td>
      </tr>

      {/* Expanded accordion prose */}
      {open && (
        <tr>
          <td colSpan={3} style={{
            padding: `${DS_SPACING.cardPadding}px`,
            borderBottom: `1px solid ${DS_BORDERS.color}`,
          }}>
            {/* Pre-call annotation (POST trigger only) */}
            {showAnnotation && (
              <div style={{
                borderLeft: `2px solid ${DS_COLORS.ai.action}`,
                paddingLeft: `${DS_SPACING.labelToContent}px`,
                marginBottom: `${DS_SPACING.paragraphSpacing}px`,
              }}>
                <p style={{
                  ...DS_TYPOGRAPHY.bodyMd,
                  color: DS_COLORS.text.secondary,
                  margin: 0,
                  fontStyle: 'italic',
                }}>
                  {param.pre_call_annotation}
                </p>
              </div>
            )}

            {/* Analysis prose */}
            {param.analysis_prose && param.analysis_prose.split('\n\n').filter((c) => c.trim()).map((chunk, idx) => (
              <p key={idx} style={{
                ...DS_TYPOGRAPHY.bodyMd,
                color: DS_COLORS.text.primary,
                margin: 0,
                marginBottom: `${DS_SPACING.paragraphSpacing}px`,
              }}>
                {chunk.trim()}
              </p>
            ))}

            {/* Gate statement */}
            {param.gate_statement && (
              <>
                <hr style={{
                  border: 'none',
                  borderTop: `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
                  margin: `${DS_SPACING.labelToContent}px 0`,
                }} />
                <p style={{
                  ...DS_TYPOGRAPHY.bodyMd,
                  color: DS_COLORS.text.muted,
                  margin: 0,
                }}>
                  {param.gate_statement}
                </p>
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Adjust Analysis Side Panel ──────────────────────────────

function AdjustAnalysisSidePanel({ open, parameters, startupId, trigger, onClose, onApplied, adjustButtonRef }) {
  const [selectedKnobs, setSelectedKnobs] = useState({});
  const [applying, setApplying] = useState(false);
  const knobStates = ACTION_BUTTONS.knobStates;

  const toggleKnob = (paramNum, knobKey) => {
    setSelectedKnobs((prev) => {
      const paramKnobs = { ...(prev[paramNum] || {}) };
      if (paramKnobs[knobKey]) {
        delete paramKnobs[knobKey];
      } else {
        paramKnobs[knobKey] = true;
      }
      return { ...prev, [paramNum]: paramKnobs };
    });
  };

  const hasAnyKnobs = Object.values(selectedKnobs).some(
    (knobs) => Object.keys(knobs).length > 0
  );

  const handleApply = async () => {
    if (applying || !hasAnyKnobs) return;
    setApplying(true);
    try {
      const triggerApi = (trigger || 'pre').toLowerCase();
      await rerunFca(startupId, triggerApi);
      toast.success(FCA_PAGE_CONFIG.rerunButton?.successToast || 'Re-analysis started.');
      onApplied?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || FCA_PAGE_CONFIG.rerunButton?.errorToast || 'Could not apply adjustments.');
      // Panel stays open on error
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    onClose();
    adjustButtonRef?.current?.focus();
  };

  return (
    <AccrezeoSheet
      open={open}
      onClose={handleClose}
      title={FCA_ADJUST.title}
      ariaLabel={FCA_ADJUST.title}
      width="400px"
      footer={
        <button
          type="button"
          onClick={handleApply}
          disabled={!hasAnyKnobs || applying}
          className={`${INTENT_TO_BUTTON.ai.bg} ${INTENT_TO_BUTTON.ai.text} ${INTENT_TO_BUTTON.ai.border} ${INTENT_TO_BUTTON.ai.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            width: '100%',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 20px',
            borderRadius: DS_BORDERS.radiusButton,
            cursor: (!hasAnyKnobs || applying) ? 'not-allowed' : 'pointer',
            opacity: (!hasAnyKnobs || applying) ? 0.5 : 1,
            transition: 'all 150ms',
          }}
        >
          {applying
            ? <><SpinIcon size={16} /> {FCA_ADJUST.reanalyseButton?.loadingLabel}</>
            : FCA_ADJUST.reanalyseButton?.label}
        </button>
      }
    >
      {(parameters || []).map((param) => {
        const verdict = resolveVerdict(param);
        const paramKnobs = selectedKnobs[param.parameter_number] || {};
        return (
          <div key={param.parameter_number} style={{
            marginBottom: `${DS_SPACING.cardPadding}px`,
            paddingBottom: `${DS_SPACING.labelToContent}px`,
            borderBottom: `1px solid ${DS_BORDERS.color}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: `${DS_SPACING.buttonGap}px`,
              marginBottom: `${DS_SPACING.labelToContent}px`,
            }}>
              <span style={{
                fontFamily: DS_TYPOGRAPHY.sectionTitle.fontFamily,
                fontSize: '14px',
                fontWeight: 600,
                color: DS_COLORS.text.primary,
                flex: 1,
              }}>
                {param.parameter_name}
              </span>
              <VerdictPillBadge verdict={verdict} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${DS_SPACING.buttonGap}px` }}>
              {FCA_KNOB_DEFS.map((knob) => {
                const active = !!paramKnobs[knob.key];
                return (
                  <button
                    key={knob.key}
                    type="button"
                    onClick={() => toggleKnob(param.parameter_number, knob.key)}
                    style={{
                      ...DS_TYPOGRAPHY.selectorBtn,
                      minHeight: `${DS_SPACING.minTouchTarget}px`,
                      padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.labelToContent}px`,
                      borderRadius: DS_BORDERS.radiusPill,
                      border: active
                        ? `2px solid ${DS_COLORS.text.brand}`
                        : `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
                      background: active
                        ? (knobStates?.active?.bg || DS_COLORS.surface.warm)
                        : (knobStates?.default?.bg || DS_COLORS.surface.white),
                      color: active
                        ? (knobStates?.active?.text || DS_COLORS.text.brand)
                        : (knobStates?.default?.text || DS_COLORS.text.secondary),
                      cursor: 'pointer',
                      transition: 'all 150ms',
                    }}
                  >
                    {knob.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </AccrezeoSheet>
  );
}

// ── Main Panel ──────────────────────────────────────────────

export default function FCADetailPanel({ startupId, startupName, trigger, isRunning, onRunComplete }) {
  const [fca, setFca] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineError, setPipelineError] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunOverlay, setRerunOverlay] = useState(false);
  const [adjustPanelOpen, setAdjustPanelOpen] = useState(false);
  const adjustButtonRef = useRef(null);

  const triggerKey = trigger?.toUpperCase() || 'PRE';
  const triggerApi = trigger?.toLowerCase() || 'pre';

  const handleRerun = async () => {
    if (rerunning) return;
    setRerunning(true);
    setRerunOverlay(true);
    try {
      await rerunFca(startupId, triggerApi);
      toast.success(FCA_BUTTONS.rerun?.loadingLabel ? (FCA_PAGE_CONFIG.rerunButton?.successToast || 'Re-analysis started.') : 'Re-analysis started.');
    } catch (err) {
      const cfg = FCA_PAGE_CONFIG.errorStates || {};
      toast.error(err?.message || cfg.pipeline_failed || 'Could not start re-analysis.');
      setRerunning(false);
      setRerunOverlay(false);
    }
  };

  const fetchFCA = useCallback(async () => {
    if (!startupId) return;
    setLoading(true);
    setError(null);
    setPipelineError(null);
    try {
      const data = await getFCAResult(startupId, triggerApi);
      if (data?.status === 'complete' || data?.status === 'partial') {
        setFca(data.fca || data);
        setPipelineRunning(false);
      } else if (data?.status === 'generating') {
        setPipelineRunning(true);
      } else if (data?.status === 'failed') {
        const errMsg = data?.fca_error || data?.error || '';
        const isDeckError = /deck|not found/i.test(errMsg);
        setPipelineError(isDeckError
          ? FCA_PAGE_CONFIG.errorStates.deck_not_found
          : FCA_PAGE_CONFIG.errorStates.pipeline_failed);
        setFca(null);
        setPipelineRunning(false);
      } else {
        setFca(null);
      }
    } catch (err) {
      setError(err.message || FCA_PAGE_CONFIG.errorStates.pipeline_failed);
    } finally {
      setLoading(false);
    }
  }, [startupId, triggerApi]);

  useEffect(() => {
    fetchFCA();
  }, [fetchFCA]);

  const showProgress = isRunning || pipelineRunning;

  // ── Loading state: skeleton ──
  if (loading && !fca && !showProgress) return <Skeleton />;

  // ── Error state ──
  if (error || pipelineError) {
    return (
      <div style={{ maxWidth: `${DS_SPACING.pageMaxWidth}px`, padding: `${DS_SPACING.cardPadding}px` }}>
        <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.error, margin: 0 }}>
          {pipelineError || error}
        </p>
        <button
          onClick={() => { setPipelineError(null); fetchFCA(); }}
          style={{
            ...DS_TYPOGRAPHY.actionBtn,
            ...ACTION_BUTTONS.rerun,
            padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.cardPadding}px`,
            cursor: 'pointer',
            marginTop: `${DS_SPACING.labelToContent}px`,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Pipeline running: poll ──
  if (showProgress) {
    return (
      <div style={{ maxWidth: `${DS_SPACING.pageMaxWidth}px`, padding: `${DS_SPACING.cardPadding}px` }}>
        <PipelineStatus
          startupId={startupId}
          trigger={triggerApi}
          onComplete={() => {
            setPipelineRunning(false);
            onRunComplete?.();
            fetchFCA();
          }}
          onFailed={(errMsg) => {
            setPipelineRunning(false);
            onRunComplete?.();
            const isDeckError = /deck|not found/i.test(errMsg || '');
            setPipelineError(isDeckError
              ? FCA_PAGE_CONFIG.errorStates.deck_not_found
              : FCA_PAGE_CONFIG.errorStates.pipeline_failed);
          }}
        />
      </div>
    );
  }

  if (!fca) {
    return (
      <div style={{ maxWidth: `${DS_SPACING.pageMaxWidth}px`, padding: `${DS_SPACING.cardPadding}px`, textAlign: 'center' }}>
        <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted }}>
          No analysis available for this trigger.
        </p>
      </div>
    );
  }

  // ── Resolve labels ──
  const recConfig = LABELS.recommendationLabels[fca.recommendation];
  const recLabel = recConfig?.displayLabel || fca.recommendation;
  const recIntent = recConfig?.colourIntent || 'neutral';
  const triggerLabel = LABELS.triggerLabels[triggerApi];
  const dateStr = fca.created_at
    ? new Date(fca.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  const hasResult = !!fca;

  return (
    <div style={{ maxWidth: `${DS_SPACING.pageMaxWidth}px`, position: 'relative' }}>

      {rerunOverlay && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          backdropFilter: `blur(${AI_REWRITING_STATE.blurAmount})`,
          WebkitBackdropFilter: `blur(${AI_REWRITING_STATE.blurAmount})`,
          background: `rgba(255,255,255,${1 - AI_REWRITING_STATE.dimOpacity})`,
          borderRadius: DS_BORDERS.radiusCard,
          pointerEvents: 'all',
        }}>
          <SpinIcon
            size={AI_REWRITING_STATE.spinnerSize}
            color={AI_REWRITING_STATE.ringColor}
          />
        </div>
      )}

      {(isRunning || rerunning) && (
        <PipelineStatus
          startupId={startupId}
          trigger={triggerApi}
          onComplete={() => {
            setRerunning(false);
            setRerunOverlay(false);
            onRunComplete?.();
            fetchFCA();
          }}
          onFailed={() => {
            setRerunning(false);
            setRerunOverlay(false);
          }}
        />
      )}

      {/* ── Re-run + Export PDF — top-right header buttons ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
        marginBottom: `${DS_SPACING.cardGap}px`,
      }}>
        {/* Re-run Analysis (AI intent = violet) */}
        <button
          type="button"
          disabled={rerunning || isRunning}
          onClick={handleRerun}
          className={`${INTENT_TO_BUTTON.ai.bg} ${INTENT_TO_BUTTON.ai.text} ${INTENT_TO_BUTTON.ai.border} ${INTENT_TO_BUTTON.ai.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 20px',
            borderRadius: DS_BORDERS.radiusButton,
            cursor: rerunning || isRunning ? 'wait' : 'pointer',
            opacity: rerunning || isRunning ? 0.6 : 1,
            transition: 'all 150ms',
          }}
        >
          {rerunning
            ? <SpinIcon size={16} />
            : null}
          {rerunning
            ? (FCA_BUTTONS.rerun?.loadingLabel)
            : (FCA_BUTTONS.rerun?.label)}
        </button>

        {/* Export PDF */}
        <button
          type="button"
          disabled={!fca || rerunning}
          onClick={() => window.open(exportFCAPdf(startupId, triggerApi), '_blank')}
          className={`${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} font-heading text-sm font-semibold border-[1.5px] border-solid`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: `${DS_SPACING.minTouchTarget}px`,
            padding: '10px 20px',
            borderRadius: DS_BORDERS.radiusButton,
            cursor: (!fca || rerunning) ? 'not-allowed' : 'pointer',
            opacity: (!fca || rerunning) ? 0.5 : 1,
            transition: 'all 150ms',
          }}
        >
          <FilePdf weight="bold" size={14} />
          {FCA_BUTTONS.exportPdf?.label}
        </button>
      </div>

      {/* ── Header — matches DS pattern: startup name + trigger chip + date ── */}
      <div style={{ marginBottom: `${DS_SPACING.cardGap}px` }}>
        <h1 style={{
          fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
          fontSize: '28px',
          fontWeight: 800,
          lineHeight: 1.15,
          letterSpacing: '-0.025em',
          color: DS_COLORS.text.primary,
          margin: 0,
        }}>
          {startupName || fca.startup_name || fca.fca_id || 'First Cut Analysis'}
        </h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: `${DS_SPACING.buttonGap}px`,
          marginTop: '6px',
        }}>
          {triggerLabel && (
            <span style={{
              ...DS_TYPOGRAPHY.monoLabel,
              color: DS_COLORS.text.muted,
            }}>
              {triggerLabel.shortLabel}
            </span>
          )}
          {triggerLabel && dateStr && (
            <span style={{ color: DS_COLORS.border.default }}>&middot;</span>
          )}
          {dateStr && (
            <span style={{
              fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
              fontSize: '12px',
              fontWeight: 400,
              color: DS_COLORS.text.muted,
            }}>
              {dateStr}
            </span>
          )}
        </div>
      </div>

      {/* Recommendation */}
      <h2 style={{
        fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
        fontSize: '28px',
        fontWeight: 800,
        color: RECOMMENDATION_COLOR[recIntent] || DS_COLORS.text.primary,
        margin: 0,
        marginBottom: `${DS_SPACING.labelToContent}px`,
      }}>
        {recLabel}
      </h2>

      {/* Directional signal */}
      {fca.directional_signal && (
        <div style={{
          background: DS_COLORS.surface.white,
          border: `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
          borderLeft: `4px solid ${DS_COLORS.text.brand}`,
          borderRadius: DS_BORDERS.radiusCard,
          padding: `${DS_SPACING.cardPadding}px`,
          marginBottom: `${DS_SPACING.cardGap}px`,
        }}>
          <p style={{
            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1.6,
            color: DS_COLORS.text.primary,
            margin: 0,
          }}>
            {fca.directional_signal}
          </p>
        </div>
      )}

      {/* ── Vertical parameter table ── */}
      {fca.parameters?.length > 0 && (
        <div style={{ marginBottom: `${DS_SPACING.cardGap}px` }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'auto',
          }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${DS_BORDERS.color}` }}>
                <th style={{
                  ...DS_TYPOGRAPHY.sectionLabel,
                  color: DS_COLORS.text.muted,
                  textAlign: 'left',
                  padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.cardPadding}px`,
                }}>{FCA_TABLE.columns?.parameter}</th>
                <th style={{
                  ...DS_TYPOGRAPHY.sectionLabel,
                  color: DS_COLORS.text.muted,
                  textAlign: 'center',
                  padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.buttonGap}px`,
                }}>{FCA_TABLE.columns?.verdict}</th>
                <th style={{
                  ...DS_TYPOGRAPHY.sectionLabel,
                  color: DS_COLORS.text.muted,
                  textAlign: 'left',
                  padding: `${DS_SPACING.buttonGap}px ${DS_SPACING.cardPadding}px`,
                }}>{FCA_TABLE.columns?.finding}</th>
              </tr>
            </thead>
            <tbody>
              {fca.parameters.map((param) => (
                <ParameterTableRow
                  key={param.parameter_number}
                  param={param}
                  trigger={triggerKey}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Gap list ── */}
      {(() => {
        const gaps = normaliseToArray(fca.gap_list);
        if (gaps.length === 0) return null;
        return (
          <div style={{ marginBottom: `${DS_SPACING.cardGap}px` }}>
            <h3 style={{
              fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
              fontSize: '18px',
              fontWeight: 600,
              color: DS_COLORS.text.primary,
              margin: 0,
              marginBottom: `${DS_SPACING.labelToContent}px`,
            }}>
              Open Questions
            </h3>
            <ul style={{ margin: 0, paddingLeft: `${DS_SPACING.cardPadding}px` }}>
              {gaps.map((gap, idx) => (
                <li key={idx} style={{
                  fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: DS_COLORS.text.primary,
                  marginBottom: `${DS_SPACING.buttonGap}px`,
                }}>
                  {gap}
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* ── Next data ask ── */}
      {(() => {
        const asks = normaliseToArray(fca.next_data_ask);
        if (!NEXT_DATA_ASK_HEADING[triggerKey] || asks.length === 0) return null;
        return (
          <div style={{ marginBottom: `${DS_SPACING.cardGap}px` }}>
            <h3 style={{
              fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
              fontSize: '18px',
              fontWeight: 600,
              color: DS_COLORS.text.primary,
              margin: 0,
              marginBottom: `${DS_SPACING.labelToContent}px`,
            }}>
              {NEXT_DATA_ASK_HEADING[triggerKey]}
            </h3>
            <ol style={{ margin: 0, paddingLeft: `${DS_SPACING.cardPadding}px` }}>
              {asks.map((ask, idx) => (
                <li key={idx} style={{
                  fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: DS_COLORS.text.primary,
                  marginBottom: `${DS_SPACING.buttonGap}px`,
                }}>
                  {ask}
                </li>
              ))}
            </ol>
          </div>
        );
      })()}

      {/* ── Thesis signals ── */}
      {fca.thesis_signals?.length > 0 && (
        <div style={{ marginBottom: `${DS_SPACING.cardGap}px` }}>
          <h3 style={{
            fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily,
            fontSize: '18px',
            fontWeight: 600,
            color: DS_COLORS.text.primary,
            margin: 0,
            marginBottom: `${DS_SPACING.labelToContent}px`,
          }}>
            Proposed Sector Learnings
          </h3>
          {fca.thesis_signals.map((signal, idx) => {
            const typeLabel = SIGNAL_TYPE_LABELS[signal.signal_type] || signal.signal_type;

            return (
              <div key={idx} style={{
                background: DS_COLORS.surface.white,
                border: `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
                borderRadius: DS_BORDERS.radiusCard,
                padding: `${DS_SPACING.labelToContent}px ${DS_SPACING.cardPadding}px`,
                marginBottom: `${DS_SPACING.buttonGap}px`,
              }}>
                <p style={{
                  fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: DS_COLORS.text.primary,
                  margin: 0,
                  marginBottom: `${DS_SPACING.buttonGap}px`,
                }}>
                  {signal.signal_text}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: `${DS_SPACING.buttonGap}px`, flexWrap: 'wrap' }}>
                  {/* Signal type badge */}
                  <span style={{
                    ...VERDICT_PILL.pill,
                    background: DS_COLORS.surface.slate,
                    color: DS_COLORS.text.secondary,
                    border: `1px solid ${DS_BORDERS.color}`,
                    borderRadius: DS_BORDERS.radiusPill,
                  }}>
                    {typeLabel}
                  </span>
                  {/* Source parameter */}
                  {signal.source_parameter && (
                    <span style={{
                      fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                      fontSize: '12px',
                      fontWeight: 400,
                      color: DS_COLORS.text.muted,
                    }}>
                      {signal.source_parameter}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Adjust Analysis button — above action bar ── */}
      {hasResult && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: `${DS_SPACING.buttonGap}px` }}>
          <button
            ref={adjustButtonRef}
            type="button"
            onClick={() => setAdjustPanelOpen(true)}
            className={`${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} font-heading text-sm font-semibold border-[1.5px] border-solid`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              minHeight: `${DS_SPACING.minTouchTarget}px`,
              padding: '10px 20px',
              borderRadius: DS_BORDERS.radiusButton,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
          >
            {FCA_ADJUST.triggerButton?.label}
          </button>
        </div>
      )}

      {/* ── Adjust Analysis side panel (AccrezeoSheet controls open/close animation) ── */}
      {fca.parameters && (
        <AdjustAnalysisSidePanel
          open={adjustPanelOpen}
          parameters={fca.parameters}
          startupId={startupId}
          trigger={trigger}
          onClose={() => setAdjustPanelOpen(false)}
          onApplied={() => {
            setRerunning(true);
            setRerunOverlay(true);
          }}
          adjustButtonRef={adjustButtonRef}
        />
      )}
    </div>
  );
}
